import React from 'react';

import ArticlesApi from 'Api/CRM/Modules/ArticlesApi';
import MissingTranslationsApi from 'Api/CRM/Modules/MissingTranslationsApi';
import MissingTranslationArticle from 'Api/Models/MissingTranslationArticle';
import TrueFalseIcon from 'components/common/TrueFalseIcon';
import useGlobalState from 'crm/GlobalStateContext';
import DeleteButton from 'crm/elements/DeleteButton';
import EditButton from 'crm/elements/EditButton';
import ViewButton from 'crm/elements/ViewButton';
import SweetAlert from 'crm/elements/alerts/SweetAlert';
import Card from 'crm/elements/common/Card';
import SortButton from 'crm/elements/common/SortButton';
import TableHead from 'crm/elements/common/TableHead';
import TableLoader from 'crm/elements/common/TableLoader';
import Select, { SelectOptions } from 'crm/elements/formElements/Select';
import Paginator from 'crm/elements/paginator';
import { PERMISSION } from 'crm/helpers/Permissions';
import useAutoPaginator from 'crm/hooks/useAutoPaginator';
import ArticleSlugLink from 'crm/pages/article/ArticleSlugLink';

type ArticlesTableBodyProps = {
    articles: MissingTranslationArticle[];
    updateArticles: (page?: number) => Promise<void>;
    partials: Boolean;
    languageNames: SelectOptions;
};

function MissingTranslationsArticlesList({ partials = false }: { partials?: boolean }) {
    const { languageNames } = useGlobalState();

    const { items, updateItems, isLoading, auto } = useAutoPaginator<MissingTranslationArticle>((page, params) => {
        if (!params.columnFilters.language_id || (params.columnFilters.language_id as any).length === 0) {
            params.columnFilters.language_id = ['value', '1'];
        }

        return MissingTranslationsApi.getMissingTranslationsForArticles(partials, page, { params });
    });

    return (
        <Card title={`Missing translations of ${partials ? 'system pages ' : ''}articles`}>
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <div />
                        <SortButton column="id" {...auto.sort} />
                        <SortButton column="title" {...auto.sort} />
                        <SortButton column="slug" {...auto.sort} />
                        <SortButton column="language_id" label="Language" {...auto.sort} />
                        <SortButton column="translation_id" {...auto.sort} />
                        <SortButton column="missing_languages" {...auto.sort} />
                        <SortButton column="is_blog" {...auto.sort} />
                        <SortButton column="is_casino_page" {...auto.sort} />
                        <SortButton column="no_deletable" {...auto.sort} />
                        <SortButton column="no_index" {...auto.sort} />
                    </TableHead>
                    <ArticlesTableBody
                        articles={items}
                        updateArticles={updateItems}
                        partials={partials}
                        languageNames={languageNames}
                    />
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

function ArticlesTableBody({ articles, updateArticles, partials, languageNames }: ArticlesTableBodyProps) {
    async function handleLanguageChange(
        translationId: number,
        languageId: number,
        title: string,
        slug: string,
        isBlog: boolean,
        isCasinoPage: boolean,
        noDeletable: boolean,
        noIndex: boolean
    ) {
        const article = await ArticlesApi.getByTranslationIdAndLanguageId(translationId, languageId);
        if (!article.id) {
            SweetAlert.warning('Translation does not exist.', 'Create a new translation').then((result) => {
                if (result === true) {
                    window.location.href = `/crea/superadmin/articles/create${
                        partials ? '/partial' : ''
                    }?translation_id=${translationId}&language_id=${languageId}&title=${encodeURIComponent(
                        title
                    )}&slug=${encodeURIComponent(slug)}&is_blog=${Number(isBlog)}&is_casino_page=${Number(
                        isCasinoPage
                    )}&no_deletable=${Number(noDeletable)}&no_index=${Number(noIndex)}`;
                }
            });

            return;
        }

        window.location.href = `/crea/superadmin/articles/${article.id}`;
    }

    return (
        <tbody>
            {articles.map((article) => (
                <tr key={article.id}>
                    <td>
                        <ViewButton url={`/articles/${article.id}`} permissions={[PERMISSION.VIEW_ARTICLE]} />
                        <EditButton url={`/articles/${article.id}/edit`} permissions={[PERMISSION.UPDATE_ARTICLE]} />
                        {!article.no_deletable && (
                            <DeleteButton
                                url={`/crea/api/article/${article.id}`}
                                action={updateArticles}
                                permissions={[PERMISSION.DELETE_ARTICLE]}
                            />
                        )}
                    </td>
                    <td>{article.id}</td>
                    <td>{article.title}</td>
                    <td>
                        <ArticleSlugLink article={article} />
                    </td>
                    <td>
                        <Select
                            options={languageNames}
                            value={article.language_id}
                            label=""
                            required
                            showLabel={false}
                            error={null}
                            handleChange={(e) => {
                                handleLanguageChange(
                                    article.translation_id,
                                    Number(e.target.value),
                                    article.title,
                                    article.slug,
                                    article.is_blog,
                                    article.is_casino_page,
                                    article.no_deletable,
                                    article.no_index
                                );
                            }}
                            field="article.language_id"
                        />
                    </td>
                    <td>{article.translation_id}</td>
                    <td>{article.missing_languages}</td>
                    <td>
                        <TrueFalseIcon status={article.is_blog} />
                    </td>
                    <td>
                        <TrueFalseIcon status={article.is_casino_page} />
                    </td>
                    <td>
                        <TrueFalseIcon status={article.no_deletable} />
                    </td>
                    <td>
                        <TrueFalseIcon status={article.no_index} />
                    </td>
                </tr>
            ))}
        </tbody>
    );
}

export default MissingTranslationsArticlesList;
